import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/apiClient';

const FileUpload = ({ onUploadSuccess, onUploadError, loading, setLoading }) => {
  const [phfFiles, setPhfFiles] = useState([]);
  const [postFiles, setPostFiles] = useState([]);
  const [proposedStartDate, setProposedStartDate] = useState('');
  const { isHRUser } = useAuth();

  const onDropPhf = useCallback((acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter(file => file.type === 'application/pdf');
    setPhfFiles(prev => [...prev, ...pdfFiles]);
  }, []);

  const { getRootProps: getPhfRootProps, getInputProps: getPhfInputProps, isDragActive: isPhfDragActive } = useDropzone({
    onDrop: onDropPhf,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true
  });

  const onDropPost = useCallback((acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter(file => file.type === 'application/pdf');
    setPostFiles(prev => [...prev, ...pdfFiles]);
  }, []);

  const { getRootProps: getPostRootProps, getInputProps: getPostInputProps, isDragActive: isPostDragActive } = useDropzone({
    onDrop: onDropPost,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true
  });

  const removePhfFile = (index) => setPhfFiles(prev => prev.filter((_, i) => i !== index));
  const removePostFile = (index) => setPostFiles(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    if (phfFiles.length === 0 || postFiles.length === 0) {
      onUploadError('Please upload at least one PHF file and one post description file');
      return;
    }
    if (!isHRUser()) {
      onUploadError('You do not have permission to upload files');
      return;
    }
    setLoading(true);
    const formData = new FormData();
    phfFiles.forEach(file => formData.append('phf_files', file));
    postFiles.forEach(file => formData.append('post_files', file));
    if (proposedStartDate) formData.append('proposed_who_start_date', proposedStartDate);

    try {
      const response = await api.uploadFiles(formData);
      if (response.data.success) {
        onUploadSuccess(response.data);
        setPhfFiles([]);
        setPostFiles([]);
      } else {
        onUploadError(response.data.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 401) onUploadError('Authentication required - please sign in');
      else if (error.response?.status === 403) onUploadError('You do not have permission to upload files');
      else onUploadError(error.response?.data?.error || error.message || 'An error occurred during upload');
    } finally {
      setLoading(false);
    }
  };

  const renderFiles = (files, remove) => files.length > 0 && (
    <div className="filelist">
      {files.map((file, index) => (
        <div className="fileitem" key={index}>
          <i className="fas fa-file-pdf fi-ic"></i>
          <span className="fi-name">{file.name}</span>
          <span className="fi-size">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
          <button type="button" className="fi-x" onClick={() => remove(index)} title="Remove">
            <i className="fas fa-xmark"></i>
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <section className="block">
      <div className="section-head">
        <h2><i className="fas fa-cloud-arrow-up"></i> Upload documents</h2>
        <span className="sub">PDF only · PHF + position descriptions</span>
      </div>
      <div className="card card-pad">
        <div className="dropgrid">
          <div {...getPhfRootProps()} className={`dz ${isPhfDragActive ? 'active' : ''}`}>
            <input {...getPhfInputProps()} />
            <div className="dz-ic"><i className="fas fa-user-tie"></i></div>
            <h4>PHF documents <span className="chip neutral">Candidate CVs</span></h4>
            <div className="dz-hint">{isPhfDragActive ? 'Drop PHF files here…' : 'Drag & drop PHF files here, or click to select'}</div>
            <div className="dz-sub">PDF files only</div>
            {renderFiles(phfFiles, removePhfFile)}
          </div>

          <div {...getPostRootProps()} className={`dz ${isPostDragActive ? 'active' : ''}`}>
            <input {...getPostInputProps()} />
            <div className="dz-ic"><i className="fas fa-briefcase"></i></div>
            <h4>Position descriptions <span className="chip neutral">Job posts</span></h4>
            <div className="dz-hint">{isPostDragActive ? 'Drop post description files here…' : 'Drag & drop post description files here, or click to select'}</div>
            <div className="dz-sub">PDF files only</div>
            {renderFiles(postFiles, removePostFile)}
          </div>
        </div>

        <div className="date-row">
          <label htmlFor="proposedStartDate"><i className="fas fa-calendar-alt"></i> Proposed WHO start date</label>
          <input
            type="month"
            id="proposedStartDate"
            value={proposedStartDate}
            onChange={(e) => setProposedStartDate(e.target.value)}
            placeholder="YYYY-MM"
          />
          <span className="help">Used to calculate duration for current positions. If not set, uses today's date.</span>
        </div>

        <div className="submit-row">
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={loading || phfFiles.length === 0 || postFiles.length === 0}
          >
            {loading
              ? (<><i className="fas fa-spinner fa-spin"></i> Processing files…</>)
              : (<><i className="fas fa-gears"></i> Process documents</>)}
          </button>
        </div>
      </div>
    </section>
  );
};

export default FileUpload;
